import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Pencil, Paperclip, Download } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { AuditTrailViewer } from '../../components/AuditTrail';
import TabNavigation from '../../components/TabNavigation';

const CertificationDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchCertification();
    fetchAttachments();
  }, [id]);

  const fetchCertification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/certifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCert(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load certification');
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/certifications/${id}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(res.data.attachments || []);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setAttachments([]);
    }
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const link = document.createElement('a');
      link.href = res.data.download_url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('msword')) return '📝';
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return '📊';
    if (contentType?.includes('image/')) return '🖼️';
    return '📎';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Valid': return 'bg-green-100 text-green-700';
      case 'Expiring Soon': return 'bg-yellow-100 text-yellow-700';
      case 'Expired': return 'bg-red-100 text-red-700';
      case 'Pending Renewal': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Safety Certificate': return 'bg-blue-100 text-blue-700';
      case 'Professional License': return 'bg-purple-100 text-purple-700';
      case 'Equipment License': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    expiry.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const getExpiryColor = (expiryDate) => {
    const days = getDaysRemaining(expiryDate);
    if (days === null) return 'text-gray-900';
    if (days < 0) return 'text-red-600';
    if (days <= 30) return 'text-red-600';
    if (days <= 60) return 'text-yellow-600';
    return 'text-gray-900';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !cert) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Certification not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/training/certifications')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Certifications
          </Button>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(cert.expiry_date);

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/training/certifications')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Certifications
            </Button>

            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{cert.certificate_name}</h1>
                <p className="text-gray-600 mt-1">{cert.employee_name}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(cert.status)}`}>
                  {cert.status}
                </span>
                {cert.linked_training_id && (
                  <span className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                    Linked to Training
                  </span>
                )}
                {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/training/certifications?edit=${id}`)}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'audit'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Certification Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Employee Name</label>
                    <p className="text-gray-900 mt-1">{cert.employee_name || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Certificate Type</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(cert.certificate_type)}`}>
                        {cert.certificate_type}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Status</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(cert.status)}`}>
                        {cert.status}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Issuing Body</label>
                    <p className="text-gray-900 mt-1">{cert.issuing_body || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Certificate Number</label>
                    <p className="text-gray-900 mt-1 font-mono">{cert.certificate_number || '—'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Issue Date</label>
                    <p className="text-gray-900 mt-1">{formatDate(cert.issue_date)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Expiry Date</label>
                    <p className={`mt-1 font-semibold ${getExpiryColor(cert.expiry_date)}`}>
                      {cert.expiry_date ? formatDate(cert.expiry_date) : '—'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Validity Period (months)</label>
                    <p className="text-gray-900 mt-1">{cert.validity_period_months || '—'}</p>
                  </div>

                  {daysRemaining !== null && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Days Remaining</label>
                      <p className={`mt-1 text-lg font-bold ${
                        daysRemaining < 0 ? 'text-red-600' :
                        daysRemaining <= 30 ? 'text-red-600' :
                        daysRemaining <= 60 ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {daysRemaining < 0 ? `Expired ${Math.abs(daysRemaining)} days ago` : `${daysRemaining} days`}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Auto-Renewal</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        cert.auto_renewal ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {cert.auto_renewal ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Created</label>
                    <p className="text-gray-900 mt-1">{formatDateTime(cert.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            {attachments.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Attachments
                    <span className="text-sm font-normal text-gray-500">({attachments.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {attachments.map((file) => (
                      <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="text-lg flex-shrink-0">{getFileIcon(file.content_type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{file.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.file_size)}
                            {file.uploaded_by && ` · Uploaded by ${file.uploaded_by}`}
                            {file.uploaded_at && ` · ${formatDateTime(file.uploaded_at)}`}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAttachment(file.id, file.filename)}
                          className="flex-shrink-0"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <AuditTrailViewer
                  entityType="certification"
                  entityId={id}
                  title="Certification Audit History"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificationDetail;
